import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Home, Users, ClipboardList, Building2, Settings, Search, 
  Menu, Sun, Moon, LogOut, Globe
} from '../Icons';

const Sidebar = ({ onOpenCommandPalette }) => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [canHover, setCanHover] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHover(!!mql.matches);
    update();
    if (mql.addEventListener) mql.addEventListener('change', update);
    else mql.addListener(update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', update);
      else mql.removeListener(update);
    };
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Handle escape key to close mobile sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/players', icon: Users, label: 'Players' },
    { path: '/reports', icon: ClipboardList, label: 'Reports' },
    { path: '/portfolio', icon: Globe, label: 'Portfolio' },
  ];

  const adminItems = [
    { path: '/organization', icon: Building2, label: 'Organization' },
  ];

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            display: 'none',
          }}
        />
      )}

      {/* Mobile hamburger button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu"
        style={{
          display: 'none',
          position: 'fixed',
          top: 'var(--space-4)',
          left: 'var(--space-4)',
          zIndex: 101,
          width: '40px',
          height: '40px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
        }}
      >
        <Menu size={20} />
      </button>

      <aside 
        className={`app-sidebar ${expanded ? 'expanded' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
        onMouseEnter={canHover ? () => setExpanded(true) : undefined}
        onMouseLeave={canHover ? () => setExpanded(false) : undefined}
      >
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">⚽</div>
          <span className="sidebar-logo-text">Nexus Scout</span>
        </div>

        {/* Command Palette Trigger */}
        <div className="sidebar-nav">
          <div className="sidebar-section">
            <button
              className="sidebar-item"
              onClick={onOpenCommandPalette}
              style={{ 
                width: '100%', 
                border: 'none', 
                textAlign: 'left',
                background: 'transparent'
              }}
            >
              <span className="sidebar-item-icon"><Search size={20} /></span>
              <span className="sidebar-item-text">Search</span>
            </button>
          </div>

          {/* Main Navigation */}
          <div className="sidebar-section">
            <div className="sidebar-section-title">Navigation</div>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
              >
                <span className="sidebar-item-icon"><item.icon size={20} /></span>
                <span className="sidebar-item-text">{item.label}</span>
              </Link>
            ))}
          </div>

          {/* Admin Section */}
          {isAdmin && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Admin</div>
              {adminItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                >
                  <span className="sidebar-item-icon"><item.icon size={20} /></span>
                  <span className="sidebar-item-text">{item.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Theme Toggle */}
          <ThemeToggle expanded={expanded} />
          
          {/* Settings */}
          <Link
            to="/settings"
            className={`sidebar-item ${isActive('/settings') || isActive('/profile') ? 'active' : ''}`}
          >
            <span className="sidebar-item-icon"><Settings size={20} /></span>
            <span className="sidebar-item-text">Settings</span>
          </Link>

          {/* Sign Out */}
          <button
            type="button"
            className="sidebar-item"
            onClick={handleLogout}
            style={{ 
              width: '100%',
              border: 'none',
              textAlign: 'left',
              background: 'transparent'
            }}
          >
            <span className="sidebar-item-icon"><LogOut size={20} /></span>
            <span className="sidebar-item-text">Sign out</span>
          </button>

          {/* User Profile */}
          <Link to="/profile" className="sidebar-user">
            <div className="sidebar-user-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt="" />
              ) : (
                user?.name?.charAt(0)?.toUpperCase() || '?'
              )}
            </div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name || 'User'}</div>
              <div className="sidebar-user-role">{user?.role || 'Scout'}</div>
            </div>
          </Link>
        </div>
      </aside>
    </>
  );
};

// Theme Toggle Component
const ThemeToggle = ({ expanded }) => {
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme || 'dark');

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = newTheme;
    setTheme(newTheme);
    try {
      localStorage.setItem('theme', newTheme);
    } catch (e) {
      // Ignore storage errors
    }
  };

  return (
    <button
      className="sidebar-item"
      onClick={toggleTheme}
      style={{ 
        width: '100%', 
        border: 'none', 
        textAlign: 'left',
        background: 'transparent',
        marginBottom: 'var(--space-2)'
      }}
    >
      <span className="sidebar-item-icon">{theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}</span>
      <span className="sidebar-item-text">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
    </button>
  );
};

export default Sidebar;
